import React, { useState, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Eye, EyeOff, GripVertical, ChevronDown, ChevronRight,
  ArrowUp, ArrowDown, Copy, Settings
} from 'lucide-react';
import { DEFAULT_SECTIONS } from '../../contexts/EditorContext';

/* ═══════════════════════════════════════════ */
/*           SORTABLE SECTION ITEM             */
/* ═══════════════════════════════════════════ */
function SortableSectionItem({ 
  section, 
  isExpanded, 
  onToggleExpand, 
  onToggleVisibility,
  onCopy,
  children,
  isDragOverlay = false
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const s = section.settings;
  const isVisible = s?.visible !== false;

  return (
    <div
      ref={setNodeRef}
      style={isDragOverlay ? {} : style}
      className={`
        border-b border-white/5 
        ${isDragging ? 'bg-indigo-500/10 border-indigo-500/30' : ''}
        ${isDragOverlay ? 'bg-[#0C0E18] border border-indigo-500 rounded-lg shadow-2xl' : ''}
      `}
      data-testid={`section-item-${section.id}`}
    >
      {/* Section Row */}
      <div className="flex items-center gap-1 px-2 py-2.5">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="p-1.5 rounded cursor-grab active:cursor-grabbing text-white/30 hover:text-white/60 hover:bg-white/5 transition-colors touch-none"
          data-testid={`drag-handle-${section.id}`}
          title="Ziehen zum Verschieben"
        >
          <GripVertical className="w-4 h-4" />
        </button>

        {/* Visibility Toggle */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleVisibility(section.id); }}
          className={`p-1.5 rounded transition-colors ${
            isVisible 
              ? 'text-white/50 hover:text-white/80' 
              : 'text-red-400/60 hover:text-red-400'
          }`}
          data-testid={`visibility-${section.id}`}
          title={isVisible ? 'Ausblenden' : 'Einblenden'}
        >
          {isVisible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
        </button>

        {/* Label */}
        <button
          onClick={() => onToggleExpand(section.id)}
          className={`flex-1 text-left text-[13px] font-medium transition-colors px-1 ${
            isVisible ? 'text-white/70' : 'text-white/30 line-through'
          }`}
        >
          {section.label}
        </button>

        {/* Position indicator */}
        <span className="text-[10px] text-white/20 font-mono px-2">
          #{(s?.order ?? 0) + 1}
        </span>

        {/* Copy button */}
        <button
          onClick={(e) => { e.stopPropagation(); onCopy(section.id); }}
          className="p-1 rounded text-white/20 hover:text-white/50 hover:bg-white/5 transition-colors"
          title="Einstellungen kopieren"
        >
          <Copy className="w-3 h-3" />
        </button>

        {/* Expand/Collapse */}
        <button
          onClick={() => onToggleExpand(section.id)}
          className="p-1 rounded text-white/30 hover:text-white/60 transition-colors"
        >
          {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Expanded Settings */}
      {isExpanded && !isDragOverlay && children}
    </div>
  );
}

/* ═══════════════════════════════════════════ */
/*         DRAG OVERLAY COMPONENT              */
/* ═══════════════════════════════════════════ */
function DragOverlayItem({ section }) {
  const s = section?.settings;
  const isVisible = s?.visible !== false;

  if (!section) return null;

  return (
    <div className="bg-[#0C0E18] border-2 border-indigo-500 rounded-lg shadow-2xl px-3 py-2.5 flex items-center gap-2">
      <GripVertical className="w-4 h-4 text-indigo-400" />
      {isVisible ? (
        <Eye className="w-3.5 h-3.5 text-white/50" />
      ) : (
        <EyeOff className="w-3.5 h-3.5 text-red-400/60" />
      )}
      <span className={`text-[13px] font-medium ${isVisible ? 'text-white' : 'text-white/30 line-through'}`}>
        {section.label}
      </span>
      <span className="text-[10px] text-indigo-400 font-mono ml-auto">
        Wird verschoben...
      </span>
    </div>
  );
}

/* ═══════════════════════════════════════════ */
/*          SORTABLE SECTIONS LIST             */
/* ═══════════════════════════════════════════ */
export function SortableSectionsList({
  sections,
  expandedSection,
  onToggleExpand,
  onToggleVisibility,
  onReorder,
  onCopy,
  renderSettings,
}) {
  const [activeId, setActiveId] = useState(null);

  // Sensors for keyboard + pointer interaction
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum drag distance before activation
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get section IDs in current order
  const sectionIds = useMemo(() => sections.map(s => s.id), [sections]);

  // Find active section for drag overlay
  const activeSection = useMemo(() => 
    sections.find(s => s.id === activeId), 
    [sections, activeId]
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = sectionIds.indexOf(active.id);
      const newIndex = sectionIds.indexOf(over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        // Calculate new order values
        const newOrder = arrayMove(sectionIds, oldIndex, newIndex);
        onReorder(newOrder);
      }
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
        <div className="relative">
          {/* Drop zone indicator when dragging */}
          {activeId && (
            <div className="absolute inset-x-0 top-0 h-1 bg-indigo-500/30 rounded pointer-events-none" />
          )}
          
          {sections.map((section) => (
            <SortableSectionItem
              key={section.id}
              section={section}
              isExpanded={expandedSection === section.id}
              onToggleExpand={onToggleExpand}
              onToggleVisibility={onToggleVisibility}
              onCopy={onCopy}
            >
              {renderSettings(section)}
            </SortableSectionItem>
          ))}
        </div>
      </SortableContext>

      {/* Drag Overlay - shows what's being dragged */}
      <DragOverlay dropAnimation={{
        duration: 200,
        easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
      }}>
        {activeId ? <DragOverlayItem section={activeSection} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

export default SortableSectionsList;
